/**
 * 상세 이미지 접기/펼치기 섹션
 *
 * 캠페인 상세 이미지가 일정 높이(500px)를 초과하면 접힘 처리하고,
 * "상세이미지 더보기" 버튼으로 펼칠 수 있다.
 */

import { useEffect, useRef, useState } from 'react'

/** 접힌 상태 최대 높이 (px) */
const COLLAPSED_HEIGHT = 500

interface DetailImageSectionProps {
  images: string[]
}

export function DetailImageSection({ images }: DetailImageSectionProps) {
  const [expanded, setExpanded] = useState(false)
  const [needsCollapse, setNeedsCollapse] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // 이미지 로드 후 실제 높이가 기준보다 큰지 확인
    const check = () => {
      if (containerRef.current) {
        setNeedsCollapse(containerRef.current.scrollHeight > COLLAPSED_HEIGHT + 100)
      }
    }
    // 이미지 로드 완료 시 체크
    const imgs = containerRef.current?.querySelectorAll('img') || []
    let loaded = 0
    imgs.forEach((img) => {
      if (img.complete) { loaded++; } else { img.addEventListener('load', () => { loaded++; if (loaded >= imgs.length) check() }, { once: true }) }
    })
    if (loaded >= imgs.length) check()
  }, [images])

  return (
    <div className="mb-4">
      <div
        ref={containerRef}
        className="relative overflow-hidden"
        style={!expanded && needsCollapse ? { maxHeight: COLLAPSED_HEIGHT } : undefined}
      >
        <div className="space-y-0">
          {images.map((url, i) => (
            <img key={i} src={url} alt={`상세 이미지 ${i + 1}`} className="w-full" />
          ))}
        </div>
        {/* 그라데이션 오버레이 */}
        {!expanded && needsCollapse && (
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
        )}
      </div>
      {needsCollapse && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full py-3 text-sm text-on-surface-variant hover:text-on-surface flex items-center justify-center gap-1 border-b border-border"
        >
          {expanded ? '상세이미지 접기' : '상세이미지 더보기'}
          <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
    </div>
  )
}
